import { FC, useContext, useEffect, useRef, useState } from "react";
import Select from "react-select";
import { useMileageReminders } from "../../context/mileageRemindersContext";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminMileagesApi } from "../../http/mileages/api";
import { useSubsidiariesApi } from "../../http/subsidiaries/api";
import { Subsidiary } from "../../http/subsidiaries/types";
import { InfoBox, WarningBox } from "../ui/Boxes";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import LoadingSpinner from "../ui/LoadingSpinner";
import ModalDialog from "../ui/ModalDialog";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Switch from "../ui/Switch";

const SUBSIDIARY_FIELDS = ["id", "name"] as const;
type SubsidiaryField = (typeof SUBSIDIARY_FIELDS)[number];
type SubsidiarySearch = Pick<Subsidiary, SubsidiaryField>;

type RecipientsOption = {
  onlyOverdue: boolean;
  label: string;
};

type SendMileageRemindersDialogProps = {
  onClose: () => void;
};

const SendMileageRemindersDialog: FC<SendMileageRemindersDialogProps> = ({ onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const mileagesApi = useAdminMileagesApi();
  const subsidiariesApi = useSubsidiariesApi();
  const mileageReminders = useMileageReminders();
  const aborter = useRef(new AbortController());
  const [selectedSubsidiaries, setSelectedSubsidiaries] = useState<SubsidiarySearch[]>([]);
  const [recipientsOption, setRecipientsOption] = useState<RecipientsOption | null>(null);
  const [updateMileageEmailSentDate, setUpdateMileageEmailSentDate] = useState(false);
  // the sending started from this dialog goes on in background, also if the dialog is closed
  const [isStarted, setIsStarted] = useState(false);

  const recipientsOptions: RecipientsOption[] = [
    { onlyOverdue: false, label: i18n.t.mileages.remindersDialog.allDrivers },
    { onlyOverdue: true, label: i18n.t.mileages.remindersDialog.onlyOverdue },
  ];

  // only the subsidiaries the user has access to
  const subsidiariesResult = subsidiariesApi.useAdminSubsidiaries(
    { limit: 100, fields: SUBSIDIARY_FIELDS },
    aborter.current.signal,
  );
  const [subsidiaries] = useDataInitializer(subsidiariesResult.data, subsidiariesResult.error);

  const recipientsResult = mileagesApi.useMileageReminderRecipients(
    aborter.current.signal,
    recipientsOption?.onlyOverdue,
    selectedSubsidiaries.map((x) => x.id),
  );
  const recipients = recipientsResult.data;

  useEffect(() => {
    subsidiariesResult.error && dispatch(showErrorNotification(i18n, subsidiariesResult.error));
  }, [subsidiariesResult.error]);

  useEffect(() => {
    recipientsResult.error && dispatch(showErrorNotification(i18n, recipientsResult.error));
  }, [recipientsResult.error]);

  // once the sending is over the result is shown by the provider: this dialog isn't needed anymore
  useEffect(() => {
    isStarted && !mileageReminders.isSending && onClose();
  }, [isStarted, mileageReminders.isSending]);

  const onSend = () => {
    // a new sending can't be started while another one is in progress, to avoid duplicated emails
    if (!recipientsOption || mileageReminders.isSending) {
      return;
    }

    mileageReminders.send({
      subsidiaryIds: selectedSubsidiaries.map((x) => x.id),
      onlyOverdue: recipientsOption.onlyOverdue,
      updateMileageEmailSentDate,
    });
    setIsStarted(true);
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={i18n.t.mileages.sendReminder}
      isSubmitting={isStarted && mileageReminders.isSending}
      actions={
        isStarted
          ? [
              {
                title: i18n.t.common.close,
                onClick: onClose,
              },
            ]
          : [
              {
                title: i18n.t.common.cancel,
                type: "secondary",
                onClick: onClose,
              },
              {
                title: i18n.t.common.send,
                disabled: mileageReminders.isSending || recipientsResult.isLoading || !recipients?.emails,
                onClick: onSend,
              },
            ]
      }
    >
      {isStarted ? (
        <FormFieldsContainer>
          <InfoBox text={i18n.t.mileages.remindersDialog.backgroundInfo} />
        </FormFieldsContainer>
      ) : (
        <FormFieldsContainer>
          {mileageReminders.isSending && <WarningBox text={i18n.t.mileages.remindersDialog.alreadySending} />}

          <div>{i18n.t.mileages.remindersDialog.question}</div>

          <FormField label={i18n.t.subsidiaries.singular} isOptional>
            {({ labelId, isInvalid }) => (
              <Select<SubsidiarySearch, true>
                inputId={labelId}
                value={selectedSubsidiaries}
                onChange={(value) => setSelectedSubsidiaries([...value])}
                options={subsidiaries?.items ?? []}
                placeholder={i18n.t.mileages.remindersDialog.allSubsidiaries}
                getOptionLabel={(x) => x.name}
                getOptionValue={(x) => x.id}
                isLoading={subsidiariesResult.isLoading}
                isMulti
                isClearable
                styles={reactSelectStyles<SubsidiarySearch, true>(isInvalid)}
                menuPosition="fixed"
              />
            )}
          </FormField>

          <FormField label={i18n.t.mileages.remindersDialog.recipients}>
            {({ labelId, isInvalid }) => (
              <Select<RecipientsOption>
                inputId={labelId}
                value={recipientsOption}
                onChange={(value) => setRecipientsOption(value)}
                options={recipientsOptions}
                placeholder={`${i18n.t.common.select}...`}
                getOptionLabel={(x) => x.label}
                getOptionValue={(x) => `${x.onlyOverdue}`}
                styles={reactSelectStyles<RecipientsOption>(isInvalid)}
                menuPosition="fixed"
              />
            )}
          </FormField>

          <InfoBox
            text={i18n.t.mileages.remindersDialog.onlyOverdueInfo(i18n.t.mileages.remindersDialog.onlyOverdue)}
          />

          {/* the number of recipients is shown once the recipients type is chosen */}
          {!!recipientsOption && recipientsResult.isLoading && <LoadingSpinner size="small" />}
          {!!recipientsOption && !!recipients && (
            <>
              <div>
                {recipients.emails > 0
                  ? i18n.t.mileages.remindersDialog.summary(recipients.drivers, recipients.emails)
                  : i18n.t.mileages.remindersDialog.noRecipients}
              </div>
              {recipients.excludedDrivers > 0 && (
                <WarningBox text={i18n.t.mileages.remindersDialog.excluded(recipients.excludedDrivers)} />
              )}
            </>
          )}

          <InfoBox
            text={i18n.t.mileages.remindersDialog.updateLastReminderInfo(
              i18n.t.mileages.reminderDialog.updateLastReminder,
            )}
          />

          <FormField label={i18n.t.mileages.reminderDialog.updateLastReminder} isOptional>
            {({ labelId }) => (
              <Switch id={labelId} checked={updateMileageEmailSentDate} onChange={setUpdateMileageEmailSentDate} />
            )}
          </FormField>
        </FormFieldsContainer>
      )}
    </ModalDialog>
  );
};

export default SendMileageRemindersDialog;
